/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { ReactNode, SyntheticEvent } from 'react';
import { Button } from 'primereact/button';
import { Severity } from './TableHeader.tsx';

export interface ActionsProps<T> {
  element: string | React.FunctionComponent<{ item: T } | undefined>;
  action?: (rowItems: T, e: SyntheticEvent) => void;
  severity?: Severity;
  help?: string;
}

interface Props<T> {
  item: T;
  actions?: Array<ActionsProps<T>>;
}

// eslint-disable-next-line
export const ActionBodyRender = <T, >({ item, actions }: Props<T>): ReactNode => {
  return (
    <div className="flex justify-content-center align-items-center">
      {actions?.map((el, idx) => {
        const { element, action, help, severity } = el;
        if (typeof element === 'string') {
          return (
            <Button
              tooltip={help}
              tooltipOptions={help ? { showDelay: 1000, position: 'bottom', className: 'text-xs' } : undefined}
              key={idx}
              size="small"
              rounded
              pt={{ root: { style: { height: '35px', width: '35px' } } }}
              className="mx-1"
              severity={severity}
              icon={element}
              onClick={(e) => action(item, e)}
            />
          );
        }
        const Component = element;
        return (
          <div key={idx} onClick={(e) => action?.(item, e)}>
            <Component item={item} />
          </div>
        );
      })}
    </div>
  );
};
